import React, { useEffect, useMemo, useState } from 'react';
import { Routes, Route, Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { Toaster as SonnerToaster } from 'sonner';
import CreateJobPage from './pages/create-job/page';
import LoginPage from './pages/loginpage';
import Dashboard from './pages/dashboard/page';
import InvestorDashboardPage from './pages/investorDashboard';
import ViewAllUsers from './pages/viewAllUsers/index';
import ProtectedRoute from './components/ProtectedRoute';
import ViewAllCompanies from './pages/ViewAllCompanies/companies';
import ViewAllJobs from './pages/ViewAllJobs/jobs';
import AddCandidates from "./pages/AddCandidates/addcandidateform";
import TalentPool from './pages/Talentpool/index';
import TalentpoolForm from "./pages/Talentpool/talentpoolform"
import ViewApplications from "./pages/ViewApplications/index";
import JobCandidates from "./pages/JobCandidates";
import DashboardLayout from './components/dashboardlayout';
import UnauthorizedPage from './pages/Unauthorized';
import { RbacProvider, useRbac } from './context';
import { isAiFunnelOnlyUser } from "./utils/aiFunnelUser";
import AssessmentsPage from './components/assessments';
import ViewCandidates from './pages/ViewCandidates';
import MyHiringManagers from './pages/MyHiringManagers';
import JoinedCandidatesList from "./pages/joinedCandidates"
import CreateSubJob from './pages/createSubJob';
import ViewSubJobs from './pages/viewSubJobs';
import JobPage from './pages/viewSubJob';
import PayoutCandidates from "./pages/payoutCandidates"
import TenureCandidates from "./pages/viewtenureapplications"
import ViewJobDetails from "./pages/dashboard/viewdetails"
import RecruiterOnboarding from "./pages/recruiteronboarding/"
import AgencyOnboarding from "./pages/agencyonboarding"
import AgencyOnboardingApprovals from "./pages/agencyOnboardingApprovals"
import TPOOnboardingForm from './pages/tpoonboardingform';
import CompanyOnboardingForm from './pages/companyonboardingform';
import  PublicTalentPoolForm  from './pages/PublicTalentPoolForm';
import {Leaderboard }from './pages/leaderboard';
import ApprochesView from './pages/approches';
import HotLeadsPage from './pages/hotLeads';
import BlockedCandidatesPage from './pages/blockedCandidates';
import CampusDriveApplications from './pages/campusdriveapplications';
import TpoView from './pages/tpopage';
import CollegeReferralProgram from './pages/CollegeReferenceProgram';
import CompanyReferralProgram from './pages/CompanyReferralProgram';
import CandidateDatabase from './pages/CandidateDatabase';
import LeadsPage from './pages/Leads';
import AIPage from './pages/aipage';
import ResultPage from './components/airesult';
import AIInterview from './pages/aiInterview';
import CompanyDetails from './pages/companyDetails';
import TransactionsForAdmin from './pages/assessmentTransactions';
import AtsChecker from './pages/AtsChecker';
import AssessmentCandidates from './pages/assessementCandidates';
import PublicJobApplications from './pages/publicjobapplications';  
import PublicSubjobApplications from './pages/PublicSubjobApplications';
import  ExternalJobApplications from './pages/ExternalJobApplications';
import { Button } from './components/ui/button';
import { Trophy, X } from 'lucide-react';
import ExamResults from './pages/viewAllUsers/Examresults/ExamResults';
import ViewProfileDetails from './components/sidebar/viewprofiledetails';
import AgreementDocumentViewer from './pages/agreementDocumentViewer';
import PublicCompanyOnboard from './pages/publicCompanyOnboard';
import PublicCompaniesView from './components/viewPublicCompenies';
import ActiveUsersDashboard from './pages/ActiveUsers';
import NotFoundPage from './pages/NotFoundPage';
import { AssignedJobs } from './pages/assignedJobs/index';
import { UserComplaints } from './pages/complaints/UserComplaints';
import { ComplaintForm } from './pages/complaints/ComplaintForm';
import { ComplaintDetail } from './pages/complaints/ComplaintDetail';
import CoordinationsPage from './pages/coordinations';
import HighlightsPage from './pages/highlights';
import ReportPage from './pages/report';
import ReportMetricDetailPage from './pages/report/MetricDetailPage';
import CompanyApproachesView from './pages/compnyApproches';
import AICallAgentPage from './pages/aiCallAgent';
import CommunicationServer from './pages/communicationServer';
import CommunicationLogs from './pages/communicationServer/logs';
import CommunicationTemplates from './pages/communicationServer/templates';
import RecruitmentMahakumb from './pages/recruitmentMahakumb';
import AdminBlogManagement from './pages/blogs/AdminBlogManagement';
import BlogForm from './pages/blogs/BlogForm';
import BlogDetail from './pages/blogs/BlogDetail';
import AdminTeams from './pages/teams/AdminTeams';
import RecruiterRequestsPage from './pages/recruiterRequests';
import OfferExperienceLetter from './pages/offerExperienceLetter';
import HunarFunnelPage from './pages/hunarFunnel';
import HunarAllocationsPage from './pages/hunarAllocations';
import HotlinePage from './pages/hotline';
import LiveSheetsPage from './pages/hotline/liveSheets';
// import Snowfall from 'react-snowfall';

// Component to check onboarding status and redirect accordingly
const DashboardGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useRbac();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (isAiFunnelOnlyUser(user)) return;
    // Redirect from onboarding pages to dashboard if already onboarded
    if (user?.isOnboarded) {
      if (window.location.pathname === '/recruiter-onboarding' || window.location.pathname === '/tpo-onboarding' || window.location.pathname === '/company-onboard') {
        navigate('/dashboard', { replace: true });
      }
    }
  }, [user, navigate]);

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const currentPath = window.location.pathname;

  if (isAiFunnelOnlyUser(user)) {
    if (currentPath !== "/hunar-funnel") {
      return <Navigate to="/hunar-funnel" replace />;
    }
    return <>{children}</>;
  }
  const isAgreementDocPath = currentPath.startsWith('/agreement-document');
  
  // Only these paths are accessible without completing onboarding
  const publicPaths = ['/login', '/unauthorized', '/'];
  const recruiterOnboardingPath = '/recruiter-onboarding';
  const tpoOnboardingPath = '/tpo-onboarding';
  const masterBdeOnboardingPath = '/bde-onboarding';
  const companyOnboardingPath = '/company-onboard';
  
  // For recruiter roles
  if (user.role === 'HM' || user.role === 'AGENCY' || user.role === 'SHM' || user.role === 'SHM_ADMIN' || user.role === 'AGENCY_ADMIN' || user.role === 'FBDE_ADMIN' || user.role === 'HR') {
    // Check isOnboarded status and only redirect if not onboarded and trying to access protected pages
    if (!user.isOnboarded && 
        !publicPaths.includes(currentPath) && 
        !isAgreementDocPath &&
        currentPath !== recruiterOnboardingPath) {
      return <Navigate to={recruiterOnboardingPath} replace />;
    }
  }
  
  // For TPO role
  if (user.role === 'TPO') {
    // Check isOnboarded status and only redirect if not onboarded and trying to access protected pages
    if (!user.isOnboarded && 
        !publicPaths.includes(currentPath) && 
        !isAgreementDocPath &&
        currentPath !== tpoOnboardingPath) {
      return <Navigate to={tpoOnboardingPath} replace />;
    }
  }

  // For COMPANY role
  if (user.role === 'COMPANY') {
    // Check isOnboarded status and only redirect if not onboarded and trying to access protected pages
    if (!user.isOnboarded && 
        !publicPaths.includes(currentPath) && 
        !isAgreementDocPath &&
        currentPath !== companyOnboardingPath) {
      return <Navigate to={companyOnboardingPath} replace />;
    }
  }

  if (user.role === 'MASTER_BDE' || user.role === 'FREELANCE_BDE' || user.role === 'BDE_INTERN') {
    // Check isOnboarded status and only redirect if not onboarded and trying to access protected pages
    if (!user.isOnboarded && 
        !publicPaths.includes(currentPath) && 
        !isAgreementDocPath &&
        currentPath !== masterBdeOnboardingPath) {
      return <Navigate to={masterBdeOnboardingPath} replace />;
    }
  }


  
  // After successful onboarding, redirect to dashboard
  if (user.isOnboarded && (currentPath === recruiterOnboardingPath || currentPath === tpoOnboardingPath)) {
    return <Navigate to="/dashboard" replace />;
  }
  
  // Always allow access to other pages if user is onboarded
  return <>{children}</>;
};

// Create a new wrapper component for protected routes
const ProtectedRouteWithGuard: React.FC<{ 
  requiredPermission?: string, 
  children: React.ReactNode 
}> = ({ requiredPermission, children }) => {
  return (
    <ProtectedRoute requiredPermission={requiredPermission}>
      <DashboardGuard>
        {children}
      </DashboardGuard>
    </ProtectedRoute>
  );
};

const CreateJobAccessGuard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { hasPermission, user } = useRbac();
  const [searchParams] = useSearchParams();
  const mode = searchParams.get("mode");
  const canAccess =
    hasPermission("createJob") ||
    Boolean(user?.canPostJobs) ||
    (mode === "edit" && hasPermission("editJob"));

  if (!canAccess) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

const RedirectInvestorFromDashboard: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useRbac();
  if (user?.role === "INVESTOR") {
    return <Navigate to="/investor-dashboard" replace />;
  }
  return <>{children}</>;
};

// Global Coming Soon Modal Component
const ComingSoonModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  message?: string;
}> = ({ isOpen, onClose, title = "Coming Soon!", message = "This feature is currently under development. Stay tuned for exciting updates!" }) => {
  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div 
        className="bg-white rounded-2xl p-6 sm:p-8 max-w-md w-full shadow-2xl transform transition-all duration-300 scale-100"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-center">
          {/* Icon */}
          <div className="mb-6 flex justify-center">
            <div className="bg-gradient-to-br from-orange-500 to-amber-600 p-4 rounded-full">
              <Trophy className="h-12 w-12 text-white" />
            </div>
          </div>
          
          {/* Title */}
          <h3 className="text-2xl font-bold text-gray-900 mb-3">{title}</h3>
          
          {/* Description */}
          <p className="text-gray-600 mb-6 leading-relaxed">
            {message}
          </p>
          
          {/* Close Button */}
          <Button
            onClick={onClose}
            className="bg-gradient-to-r from-orange-500 to-amber-600 hover:from-orange-600 hover:to-amber-700 text-white px-6 py-3 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 font-medium"
          >
            Got it!
          </Button>
        </div>
        
        {/* Close X Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={onClose}
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-gray-100 transition-colors"
        >
          <X className="h-5 w-5 text-gray-500" />
        </Button>
      </div>
    </div>
  );
};

function App() {
  return (
    <RbacProvider>
      <AppContent />
    </RbacProvider>
  );
}

// Create a new component for the app content
const AppContent = () => {
  const { user } = useRbac();
  const [showComingSoonModal, setShowComingSoonModal] = useState(false);
  const [modalConfig, setModalConfig] = useState({
    title: "Coming Soon!",
    message: "This feature is currently under development. Stay tuned for exciting updates!"
  });

  // Snowfall (commented out)
  // const isSnowfallTime = () => {
  //   const now = new Date();
  //   const minutes = now.getHours() * 60 + now.getMinutes();
  //   const start = 17 * 60 + 30; // 17:30
  //   const end = 6 * 60 + 30; // 06:30
  //   return minutes >= start || minutes <= end;
  // };
  // const [showSnowfall, setShowSnowfall] = useState<boolean>(() => isSnowfallTime());

  // Global event listener for coming soon modal
  useEffect(() => {
    const handleComingSoonEvent = (event: CustomEvent) => {
      event.preventDefault();
      
      // Set custom title and message if provided
      if (event.detail) {
        setModalConfig({
          title: event.detail.title || "Coming Soon!",
          message: event.detail.message || "This feature is currently under development. Stay tuned for exciting updates!"
        });
      }
      
      setShowComingSoonModal(true);
    };

    // Add event listeners for various features
    const events = [
      'leaderBoardClick',
      'comingSoonFeature'
    ];

    events.forEach(eventName => {
      window.addEventListener(eventName, handleComingSoonEvent as EventListener);
    });
    
    return () => {
      events.forEach(eventName => {
        window.removeEventListener(eventName, handleComingSoonEvent as EventListener);
      });
    };
  }, []);

  useEffect(() => {
    if (window.location.pathname !== '/franchise') return;

    const scriptId = 'google-gtag-aw-18110835641';
    if (!document.getElementById(scriptId)) {
      const gtagScript = document.createElement('script');
      gtagScript.id = scriptId;
      gtagScript.async = true;
      gtagScript.src = 'https://www.googletagmanager.com/gtag/js?id=AW-18110835641';
      document.head.appendChild(gtagScript);
    }

    const gtagWindow = window as Window & {
      dataLayer?: unknown[];
      gtag?: (...args: unknown[]) => void;
    };

    gtagWindow.dataLayer = gtagWindow.dataLayer || [];
    gtagWindow.gtag =
      gtagWindow.gtag ||
      ((...args: unknown[]) => {
        gtagWindow.dataLayer?.push(args);
      });

    gtagWindow.gtag('js', new Date());
    gtagWindow.gtag('config', 'AW-18110835641');
  }, []);

  // Keep snowfall window in sync without requiring a reload
  // useEffect(() => {
  //   const id = window.setInterval(() => {
  //     setShowSnowfall(isSnowfallTime());
  //   }, 60_000); // 1 minute
  //   return () => window.clearInterval(id);
  // }, []);

  const closeModal = () => {
    setShowComingSoonModal(false);
  };

  // Snowfall – custom snowflake images (commented out)
  // const images = useMemo(() => {
  //   const img1 = new Image();
  //   img1.src = "/assets/images/snow_1.png";
  //   const img2 = new Image();
  //   img2.src = "/assets/images/snow_2.png";
  //   return [img1, img2];
  // }, []);

  return (
    <div className="h-screen overflow-hidden bg-gray-50">
      {/* Snowfall commented out
      {showSnowfall && (
        <Snowfall
          color="#DDEBFF"
          images={images}
          snowflakeCount={120}
          radius={[2, 8]}
          style={{
            position: "fixed",
            width: "100vw",
            height: "100vh",
            zIndex: 50,
            pointerEvents: "none",
          }}
        />
      )}
      */}
      <Routes>
        {/* Public routes that don't need authentication */}
        <Route path="/" element={<LoginPage />} />
        <Route path="/login" element={<LoginPage />} />
        <Route path="/unauthorized" element={<UnauthorizedPage />} />
        <Route path="/jointalentpool/public/:id" element={<PublicTalentPoolForm onBack={() => {}} />} />
        <Route path="/company/createJobApplication" element={<PublicCompanyOnboard onBack={() => {}} />} />
          

        {/* Protected routes */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <RedirectInvestorFromDashboard>
                  <Dashboard />
                </RedirectInvestorFromDashboard>
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/investor-dashboard"
          element={
            <ProtectedRouteWithGuard requiredPermission="investorDashboard">
              <DashboardLayout>
                <InvestorDashboardPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/publiccompanies/applications"
          element={
             <ProtectedRouteWithGuard requiredPermission="publicCompanyApplications">
              <DashboardLayout>
                <PublicCompaniesView />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }/>
        <Route
          path="/recruiter-onboarding"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "HM" || user?.role === "AGENCY" || user?.role === "SHM" || user?.role === "SHM_ADMIN" || user?.role === "AGENCY_ADMIN" || user?.role === "FBDE_ADMIN" || user?.role === "HR"  ? (
                  user?.role === "AGENCY" ? <AgencyOnboarding /> : <RecruiterOnboarding />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/bde-onboarding"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "MASTER_BDE" || user?.role === "BDE_INTERN" || user?.role === "FREELANCE_BDE" ? (
                  <RecruiterOnboarding />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

          <Route
            path="/tpo-onboarding"
            element={ 
              <ProtectedRouteWithGuard requiredPermission="TalentPool">
                <DashboardLayout>
                  {user?.role === "TPO" ? (
                    <TPOOnboardingForm />
                  ) : (
                    <Navigate to="/TalentPool" replace />
                  )}
                </DashboardLayout>
              </ProtectedRouteWithGuard>  
            }
          />

          <Route
            path="/company-onboard"
            element={ 
              <ProtectedRouteWithGuard requiredPermission="dashboard">
                <DashboardLayout>
                  {user?.role === "COMPANY" ? (
                    <CompanyOnboardingForm />
                  ) : (
                    <Navigate to="/dashboard" replace />
                  )}
                </DashboardLayout>
              </ProtectedRouteWithGuard>  
            }
          />

           <Route
          path="/ApprochesView"
          element={
            <ProtectedRouteWithGuard requiredPermission="approchesView">
              <DashboardLayout>
                {["MASTER_BDE","FREELANCE_BDE","BDE_INTERN"].includes(user?.role as string) ?  <CompanyApproachesView /> :<ApprochesView /> }
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/ApprochesView2"
          element={
            <ProtectedRouteWithGuard requiredPermission="approchesView">
              <DashboardLayout>
                {["MASTER_BDE","FREELANCE_BDE","BDE_INTERN"].includes(user?.role as string) ?  <CompanyApproachesView /> :<ApprochesView /> }
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/hot-leads"
          element={
            <ProtectedRouteWithGuard requiredPermission="hotLeadsView">
              <DashboardLayout>
                <HotLeadsPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/hotline"
          element={
            <ProtectedRouteWithGuard requiredPermission="hotline">
              <DashboardLayout>
                <HotlinePage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/admin/live-sheets"
          element={
            <ProtectedRouteWithGuard requiredPermission="liveSheets">
              <DashboardLayout>
                <LiveSheetsPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/blocked-candidates"
          element={
            <ProtectedRouteWithGuard requiredPermission="manageBlockedCandidates">
              <DashboardLayout>
                <BlockedCandidatesPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/company-approaches"
          element={
            <ProtectedRouteWithGuard requiredPermission="companyApproaches">
              <DashboardLayout>
                <CompanyApproachesView />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/hunar-funnel"
          element={
            <ProtectedRouteWithGuard requiredPermission="hunarFunnelView">
              <DashboardLayout>
                <HunarFunnelPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/hunar-allocations"
          element={
            <ProtectedRouteWithGuard requiredPermission="hunarAllocationManage">
              <DashboardLayout>
                <HunarAllocationsPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/campus-drive-applications"
          element={
            <ProtectedRouteWithGuard requiredPermission="campusDriveApplications">
              <DashboardLayout>
                <CampusDriveApplications />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />


          
           <Route
          path="/exam/candidates"
          element={
            <ProtectedRouteWithGuard requiredPermission="exam">
              <DashboardLayout>
                <ExamResults />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        


        <Route
          path="/viewdetails/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <ViewJobDetails />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/ai-interview"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <AIInterview />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/create-job"
          element={
            <ProtectedRoute>
              <DashboardGuard>
                <CreateJobAccessGuard>
                  <DashboardLayout>
                    <ViewAllJobs />
                  </DashboardLayout>
                </CreateJobAccessGuard>
              </DashboardGuard>
            </ProtectedRoute>
          }
        />

        <Route
          path="/view-profile"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {/* Pass the required 'data' prop to ViewProfileDetails */}
                <ViewProfileDetails  />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/create-subjob"
          element={
            <ProtectedRouteWithGuard requiredPermission="createSubJob">
              <DashboardLayout>
                <CreateSubJob />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

         <Route
          path="/ExternalJobApplications"
          element={
            <ProtectedRouteWithGuard requiredPermission="externalJobApplications">
              <DashboardLayout>
                <ExternalJobApplications />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

         <Route
          path="/PublicJobApplications"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <PublicJobApplications />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

          <Route
          path="/PublicSubjobApplications"
          element={
            <ProtectedRouteWithGuard requiredPermission="publicSubjobApplications">
              <DashboardLayout>
                <PublicSubjobApplications />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        
        <Route
          path="/myHiringManagers"
          element={
            <ProtectedRouteWithGuard requiredPermission="myHiringManagers">
              <DashboardLayout>
                {['ADMIN', 'SHM', 'SHM_ADMIN', 'AGENCY_ADMIN', 'FBDE_ADMIN', 'AGENCY', 'HM', 'HR', 'FBDE', 'CONSULTANT'].includes(
                  user?.role as string
                ) ? (
                  <MyHiringManagers />
                ) : (
                  <Navigate to="/unauthorized" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/companies"
          element={
            <ProtectedRouteWithGuard requiredPermission="companies">
              <DashboardLayout>
                <ViewAllCompanies />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/companies/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="companies">
              <DashboardLayout>
                <CompanyDetails />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/transactionsOfAssessment"
          element={
            <ProtectedRouteWithGuard requiredPermission="transactions">
              <DashboardLayout>
                <TransactionsForAdmin />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/candidatesOfAssessment"
          element={
            <ProtectedRouteWithGuard requiredPermission="candidatesOfAssessment">
              <DashboardLayout>
                <AssessmentCandidates />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/users"
          element={
            <ProtectedRouteWithGuard requiredPermission="users">
              <DashboardLayout>
                <ViewAllUsers />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/viewSubJobs"
          element={
            <ProtectedRouteWithGuard requiredPermission="ViewSubJobs">
              <DashboardLayout>
                <ViewSubJobs />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/viewSubJob/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="ViewSubJobs">
              <DashboardLayout>
                <JobPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/add-candidate"
          element={
            <ProtectedRouteWithGuard requiredPermission="addCandidates">
              <DashboardLayout>
                <AddCandidates onBack={() => {}} onSubmit={(candidateData) => {}} />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/view-applications"
          element={
            <ProtectedRouteWithGuard requiredPermission="viewApplications">
              <DashboardLayout>
                <ViewApplications onAddCandidate={() => {}} />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/job-candidates/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="viewJobCandidates">
              <DashboardLayout>
                <JobCandidates />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/view-candidates"
          element={
            <ProtectedRouteWithGuard requiredPermission="viewCandidates">
              <DashboardLayout>
                <ViewCandidates />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/view-JoinedCandidates"
          element={
            <ProtectedRouteWithGuard requiredPermission="joinedCandidates">
              <DashboardLayout>
                <JoinedCandidatesList />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/offer-experience-letter"
          element={
            <ProtectedRouteWithGuard requiredPermission="joinedCandidates">
              <DashboardLayout>
                {["ADMIN", "SHM", "SHM_ADMIN", "FBDE_ADMIN", "FBDE", "HM", "HR"].includes(user?.role as string) ? (
                  <OfferExperienceLetter />
                ) : (
                  <Navigate to="/unauthorized" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/payout-candidates"
          element={
            <ProtectedRouteWithGuard requiredPermission="PayoutCandidates">
              <DashboardLayout>
                <PayoutCandidates />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/tenure-candidates"
          element={
            <ProtectedRouteWithGuard requiredPermission="TenureCandidates">
              <DashboardLayout>
                <TenureCandidates />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/TalentPool"
          element={
            <ProtectedRouteWithGuard requiredPermission="TalentPool">
              <DashboardLayout>
                <TalentPool />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        {/* Remove or comment out the leaderboard route to prevent navigation */}
        
        <Route
          path="/leaderboard"
          element={
            <ProtectedRouteWithGuard requiredPermission="leaderBoard">
              <DashboardLayout>
                <Leaderboard />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
       

        <Route
          path="/ai-mode"
          element={
            <ProtectedRouteWithGuard requiredPermission="aiMode">
              <DashboardLayout>
                <AIPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

         <Route
          path="/result"
          element={
            <ProtectedRouteWithGuard requiredPermission="aiMode">
              <DashboardLayout>
                <ResultPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/talentpoolform"
          element={
            <ProtectedRouteWithGuard requiredPermission="TalentPool">
              <DashboardLayout>
                <TalentpoolForm onBack={() => {}} onSubmit={(formData: any) => {}} />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

          <Route
          path="/Tpos"
          element={
            <ProtectedRouteWithGuard requiredPermission="TpoView">
              <DashboardLayout>
                <TpoView />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/college-referral-program"
          element={
            <ProtectedRouteWithGuard requiredPermission="collegeReferralProgram">
              <DashboardLayout>
                <CollegeReferralProgram />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/company-referral-program"
          element={
            <ProtectedRouteWithGuard requiredPermission="companyReferralProgram">
              <DashboardLayout>
                <CompanyReferralProgram />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/candidate-database"
          element={
            <ProtectedRouteWithGuard requiredPermission="candidateDatabase">
              <DashboardLayout>
              <CandidateDatabase />
            </DashboardLayout>
            </ProtectedRouteWithGuard>
            
          }
        />

        <Route
          path="/leads"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? <LeadsPage /> : <Navigate to="/dashboard" replace />}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/assessments"
          element={
            <ProtectedRoute>
              <DashboardLayout>
                <AssessmentsPage />
              </DashboardLayout>
            </ProtectedRoute>
          }
        />


        <Route
          path="/agreement-document/:id"
          element={
            <ProtectedRouteWithGuard>
              <AgreementDocumentViewer />
            </ProtectedRouteWithGuard>
          }
        />

         <Route
          path='/assignedJobs'
          element={
            <ProtectedRouteWithGuard requiredPermission="assignedJobs">
              <DashboardLayout>
                <AssignedJobs />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/ats-checker"
          element={
            <ProtectedRouteWithGuard requiredPermission="atsChecker">
              <DashboardLayout>
                <AtsChecker />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/coordinations"
          element={
            <ProtectedRouteWithGuard requiredPermission="coordinations">
              <DashboardLayout>
                <CoordinationsPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/recruitment-mahakumb"
          element={
            <ProtectedRouteWithGuard requiredPermission="recruitmentMahakumb">
              <DashboardLayout>
                <RecruitmentMahakumb />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/highlights"
          element={
            <ProtectedRouteWithGuard requiredPermission="highlights">
              <DashboardLayout>
                <HighlightsPage />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/report"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {["ADMIN", "FBDE", "SHM", "SHM_ADMIN", "FBDE_ADMIN", "HR", "HM"].includes(
                  user?.role || ""
                ) ? (
                  <ReportPage />
                ) : (
                  <Navigate to="/unauthorized" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/report/metric/:metricKey"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {["ADMIN", "FBDE", "SHM", "SHM_ADMIN", "FBDE_ADMIN", "HR", "HM"].includes(
                  user?.role || ""
                ) ? (
                  <ReportMetricDetailPage />
                ) : (
                  <Navigate to="/unauthorized" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/ai-call-agent"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <AICallAgentPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/admin/agency-onboarding"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <AgencyOnboardingApprovals />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/admin/recruiter-requests"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <RecruiterRequestsPage />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/communication-server"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <CommunicationServer />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/communication-server/logs"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <CommunicationLogs />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/communication-server/templates"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                <CommunicationTemplates />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        {/* Complaint Routes */}
        <Route
          path="/complaints"
          element={
            <ProtectedRouteWithGuard requiredPermission="complaints">
              <DashboardLayout>
                <UserComplaints />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        
        <Route
          path="/complaints/new"
          element={
            <ProtectedRouteWithGuard requiredPermission="complaints">
              <DashboardLayout>
                <ComplaintForm />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/complaints/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="complaints">
              <DashboardLayout>
                <ComplaintDetail />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/admin/complaints"
          element={
            <ProtectedRouteWithGuard requiredPermission="manageComplaints">
              <DashboardLayout>
                <UserComplaints />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/admin/complaints/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="manageComplaints">
              <DashboardLayout>
                <ComplaintDetail />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        {/* Blog Management Routes */}
        <Route
          path="/admin/blogs"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <AdminBlogManagement />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/admin/blogs/create"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <BlogForm />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/admin/blogs/:id"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <BlogDetail />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />
        <Route
          path="/admin/blogs/:id/edit"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <BlogForm />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        <Route
          path="/admin/teams"
          element={
            <ProtectedRouteWithGuard requiredPermission="dashboard">
              <DashboardLayout>
                {user?.role === "ADMIN" ? (
                  <AdminTeams />
                ) : (
                  <Navigate to="/dashboard" replace />
                )}
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

        {/* Active Users Route */}
        <Route
          path="/active-users"
          element={
            <ProtectedRouteWithGuard requiredPermission="activeUsers">
              <DashboardLayout>
                <ActiveUsersDashboard />
              </DashboardLayout>
            </ProtectedRouteWithGuard>
          }
        />

      {/* <Route path="*" element={<NotFoundPage/>} /> */}
      </Routes>

      {/* Global Coming Soon Modal */}
      <ComingSoonModal
        isOpen={showComingSoonModal}
        onClose={closeModal}
        title={modalConfig.title}
        message={modalConfig.message}
      />

      <Toaster
        position="top-center"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          duration: 4000,
          style: {
            background: "#fff",
            color: "#363636",
            fontSize: "14px",
            fontWeight: "500",
            padding: "16px",
            borderRadius: "12px",
            boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)",
            border: "1px solid #e5e7eb",
            maxWidth: "500px",
          },
          success: {
            style: {
              border: "1px solid #10b981",
              backgroundColor: "#f0fdf4",
            },
            icon: null,
          },
          error: {
            style: {
              border: "1px solid #ef4444",
              backgroundColor: "#fef2f2",
            },
            icon: null,
          },
          loading: {
            style: {
              border: "1px solid #3b82f6",
              backgroundColor: "#eff6ff",
            },
            icon: null,
          },
        }}
      />
      <SonnerToaster position="top-center" />
    </div>
  );
};

export default App;